/**
 * *Set 3D Model* (`equipment-page-bulk-actions`, R6): the asset card's model picker over the
 * selection, with *Use Product's* first. A reading on frame 07's rename popover — the frame draws
 * the button, not what opens (`PROVENANCE.md`). The writes are `useEquipmentBulkWrites.setModel`'s.
 *
 * The picker lives in `ModelPicker`, a child of the popover: `Popover` renders nothing while closed,
 * so the model list is read, and the picker's hooks run, only while it is open.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Combobox, Field, type ComboboxOption } from '@repo/design-system';
import {
  MODEL_PICKER_EXACT_MATCH,
  MODEL_PICKER_FILTER,
  modelPickerEmptyText,
  useModelPickerOptions,
} from '@repo/ui';

import { BulkPopoverFrame } from './BulkPopoverFrame';

/** *Use Product's*. Never a model file: every file the server accepts ends `.glb`. */
export const USE_PRODUCTS = 'use-products';
export const USE_PRODUCTS_LABEL = 'Use Product’s';
export const MODELS_UNREADABLE = 'Couldn’t load the 3D models';

type LoadModelFiles = (options?: { refresh?: boolean }) => Promise<string[]>;

const ModelPicker: React.FC<{
  loadModelFiles: LoadModelFiles;
  choice: string | null;
  onChoice: (choice: string | null) => void;
}> = ({ loadModelFiles, choice, onChoice }) => {
  const [modelFiles, setModelFiles] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (refresh: boolean) => {
      setLoading(true);
      setError(null);
      try {
        setModelFiles(await loadModelFiles(refresh ? { refresh: true } : undefined));
      } catch (failure) {
        setError(MODELS_UNREADABLE);
        console.warn('[equipment] model files unreadable', {
          status: (failure as { status?: unknown } | null)?.status ?? null,
        });
      } finally {
        setLoading(false);
      }
    },
    [loadModelFiles],
  );

  // Read on opening (the page's `getModelFiles` caches for 60 s); *Try again* re-reads.
  useEffect(() => {
    void load(false);
  }, [load]);

  const fileOptions = useModelPickerOptions(modelFiles ?? [], 'register');
  const options: ComboboxOption[] = [
    { value: USE_PRODUCTS, label: USE_PRODUCTS_LABEL },
    ...fileOptions,
  ];

  return (
    <Field label="3D model">
      {wiring => (
        <Combobox
          id={wiring.id}
          labelId={wiring.labelId}
          aria-describedby={wiring['aria-describedby']}
          size="sm"
          options={options}
          value={choice ?? undefined}
          onChange={onChoice}
          filter={MODEL_PICKER_FILTER}
          isExactMatch={MODEL_PICKER_EXACT_MATCH}
          maxResults={Infinity}
          commitOnClose={false}
          loading={loading}
          loadingText="Loading models…"
          error={error ?? undefined}
          onRetry={() => void load(true)}
          emptyText={modelPickerEmptyText(modelFiles, fileOptions.length)}
          placeholder="Choose a model"
        />
      )}
    </Field>
  );
};

export interface EquipmentSetModelPopoverProps {
  id: string;
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  count: number;
  /** The page's `getModelFiles` (60 s cache; `refresh` re-reads). */
  loadModelFiles: LoadModelFiles;
  busy: boolean;
  /** `null` is *Use Product's*. */
  onSet: (choice: string | null) => void;
}

export const EquipmentSetModelPopover: React.FC<EquipmentSetModelPopoverProps> = ({
  id,
  open,
  onClose,
  anchorRef,
  count,
  loadModelFiles,
  busy,
  onSet,
}) => {
  const [choice, setChoice] = useState<string | null>(null);
  useEffect(() => {
    if (open) setChoice(null);
  }, [open]);

  return (
    <BulkPopoverFrame
      id={id}
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      glyph="cube"
      title={`Set the 3D model of ${count}`}
      primaryLabel={`Set ${count}`}
      onPrimary={() => {
        if (choice !== null) onSet(choice === USE_PRODUCTS ? null : choice);
      }}
      primaryUnavailable={choice === null ? 'choose a 3D model' : null}
      busy={busy}
    >
      <ModelPicker loadModelFiles={loadModelFiles} choice={choice} onChoice={setChoice} />
    </BulkPopoverFrame>
  );
};

export default EquipmentSetModelPopover;
